import Spinner from '@atlaskit/spinner';
import { useAuthState } from 'core/providers/AuthProvider';
import DeliveriesRouting from 'modules/deliveries/deliveries-routing';
import DriverRouting from 'modules/driver/driver-routing';
import LoginRouting from 'modules/login/login-routing';
import ReportingRouting from 'modules/dashboard/dashboard-routing';
import RoutesRouting from 'modules/routes/routes-routing';
import { Navigate, Route, Routes } from 'react-router-dom';
import AuthenticatedRoute from './authenticated-route';
import PublicRoute from './public-route';
import RoleHandler from './role-handler';
import ForgotPasswordRouting from 'modules/forgot-password/forgot-password-routing';
import { useLoadScript } from '@react-google-maps/api';
import SettingsRouting from 'modules/settings/settings-routing';
import MessagesRouting from 'modules/driver-messages/driver-messages-routing';
import { DriverMessagesProvider } from 'core/providers/DriverMessagesProvider';
import { Role } from 'core/config/roles';
import VehicleChecksRouting from 'modules/vehicle-checks/vehicle-checks-routing';
import AlertsRouting from 'modules/alerts/alerts-routing';
import RunActivityRouting from 'modules/run-activity/run-activity-routing';
import UnallocatedDeliveriesRouting from 'modules/unallocated/deliveries-routing';
import TransportRouting from 'modules/transport/transport-routing';
import RdcRouting from 'modules/rdc/rdc-routing';
import DevToolsRouting from 'modules/dev-tools/dev-tools-routing';
import DeliveryRequestsList from 'modules/delivery-requests/delivery-requests-list';
import { isDevEnvironment } from 'core/utilities/environment';

const AppRouting = () => {
  const { authChecked, userRoles } = useAuthState();
  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY!,
  });

  const getDefaultPath = () => {
    const driverRoles = [Role.PHARMACY_DRIVER, Role.LOGISTICS_DRIVER];
    const hasOnlyDriverRoles = !!userRoles?.length && userRoles.every((role) => driverRoles.includes(role));

    // Drivers who also hold an admin role land on an admin page, not the driver view.
    if (hasOnlyDriverRoles) {
      return 'driver';
    }

    if (userRoles?.includes(Role.TRANSPORT_ADMIN)) {
      return 'transport';
    }

    if (userRoles?.includes(Role.LOGISTICS_ADMIN)) {
      return 'run-activity';
    }

    return 'deliveries';
  };

  if (!authChecked || !isLoaded) {
    return (
      <div className='absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'>
        <Spinner size={60} />
      </div>
    );
  }

  return (
    <Routes>
      <Route element={<PublicRoute />}>
        <Route path='login/*' element={<LoginRouting />} />
        <Route path='forgot-password/*' element={<ForgotPasswordRouting />} />
      </Route>

      <Route element={<AuthenticatedRoute />}>
        <Route element={<DriverMessagesProvider />}>
          <Route element={<RoleHandler roles={[Role.SUPER_ADMIN, Role.PHARMACY_ADMIN, Role.LOGISTICS_ADMIN]} />}>
            <Route path='settings/*' element={<SettingsRouting />} />
          </Route>

          <Route element={<RoleHandler roles={[Role.SUPER_ADMIN, Role.PHARMACY_ADMIN, Role.PHARMACY_DISPENSER]} />}>
            <Route path='deliveries/*' element={<DeliveriesRouting />} />
            <Route path='unallocated/*' element={<UnallocatedDeliveriesRouting />} />
            <Route path='routes/*' element={<RoutesRouting />} />
            <Route path='driver-messages/*' element={<MessagesRouting />} />
          </Route>

          {/* Wider than the pharmacy screens above: the Dashboard now carries
              a Delivery Requests tab, which the logistics and transport teams
              own. Which tabs a person actually sees is decided per tab in
              core/constants/dashboard-tab.ts. */}
          <Route
            element={
              <RoleHandler
                roles={[
                  Role.SUPER_ADMIN,
                  Role.PHARMACY_ADMIN,
                  Role.PHARMACY_DISPENSER,
                  Role.LOGISTICS_ADMIN,
                  Role.TRANSPORT_ADMIN,
                ]}
              />
            }
          >
            <Route path='dashboard/*' element={<ReportingRouting />} />
          </Route>

          <Route element={<RoleHandler roles={[Role.SUPER_ADMIN, Role.TRANSPORT_ADMIN]} />}>
            <Route path='transport/*' element={<TransportRouting />} />
          </Route>

          <Route element={<RoleHandler roles={[Role.SUPER_ADMIN, Role.LOGISTICS_ADMIN]} />}>
            <Route path='vehicle-checks/*' element={<VehicleChecksRouting />} />
            <Route path='alerts/*' element={<AlertsRouting />} />
            <Route path='run-activity/*' element={<RunActivityRouting />} />
            <Route path='rdc/*' element={<RdcRouting />} />
          </Route>

          <Route element={<RoleHandler roles={[Role.PHARMACY_DRIVER, Role.LOGISTICS_DRIVER]} />}>
            <Route path='driver/*' element={<DriverRouting />} />
          </Route>

          {/* The same roles the sidebar shows it to and the Firestore rules let
              read deliveryRequests. */}
          <Route element={<RoleHandler roles={[Role.SUPER_ADMIN, Role.LOGISTICS_ADMIN, Role.TRANSPORT_ADMIN]} />}>
            <Route path='delivery-requests' element={<DeliveryRequestsList />} />
          </Route>

          {/* Dev tooling. Never registered in production. */}
          {isDevEnvironment() && (
            <Route element={<RoleHandler roles={[Role.SUPER_ADMIN]} />}>
              <Route path='dev-tools/*' element={<DevToolsRouting />} />
            </Route>
          )}
        </Route>
      </Route>

      <Route path='' element={<Navigate replace to={getDefaultPath()} />} />
      <Route path='*' element={<Navigate replace to={getDefaultPath()} />} />
    </Routes>
  );
};

export default AppRouting;
